/**
 * LifeFinder Beacon Downlink Generator
 * Copyright (C) 2025, Lifefinder Systems International AB. All rights reserved.
 *
 * Generates LoRaWAN downlink payloads for configuring:
 * 1. ESP32 beacon settings (LoRa port 48)
 * 2. Dots board app settings (LoRa port 2) — schema-driven via VSM mapData
 *
 * Port 48 format: [COUNT] [CMD1] [DATA1] [CMD2] [DATA2] ...
 * Port 2 format:  [ID (1 byte)] [VALUE (4 bytes big-endian)] per field
 *
 * Dots board field definitions are read at runtime from the device's own
 * data.vsm.schema string (populated by lfndr-mqtt on every uplink), rather
 * than being hardcoded here.  Use parseVsmSchema() to extract the input
 * fields, then encodeDotsField() to build the wire bytes for each one.
 */

export const LORA_PORT_BEACON = 48; // ESP32 beacon settings
export const LORA_PORT_DOTS = 2; // Dots board app settings (non-persistent / debug)
export const LORA_PORT_PERSISTENT = 7; // Dots board persistent customization settings

// Command bytes
export const CMD = {
  SET_BEACON_MODE: 0x01,
  SET_MAC: 0x02,
  SET_SSID: 0x03,
  SET_PASSWORD: 0x04,
  SET_BEACON_INTERVAL: 0x05,
  SET_OUTPUT_POWER: 0x06,
  SET_CHANNEL: 0x07,
  SET_PROTOCOL: 0x08,
  SET_DATA_RATE: 0x09,
  SET_BEACON_JITTER: 0x0a,
  GET_STATUS: 0x0b,
  APPLY_SETTINGS: 0x0c,
  RESET_TO_FALLBACK: 0xf5,
} as const;

// WiFi protocol flags
export const WIFI_PROTOCOL = {
  B: 0x01,
  G: 0x02,
  N: 0x04,
  BG: 0x03,
  BGN: 0x07,
} as const;

// Valid data rates (LR1110 compatible only)
export const VALID_DATA_RATES: Record<string, number> = {
  '1': 0x01,
  '2': 0x02,
  '6': 0x06,
  '9': 0x09,
  '12': 0x0c,
  '18': 0x12,
  '24': 0x18,
  '36': 0x24,
  '48': 0x30,
  '54': 0x36,
  MCS0: 0x00,
  MCS1: 0xff,
  MCS2: 0xfe,
  MCS3: 0xfd,
  MCS4: 0xfc,
  MCS5: 0xfb,
};

// ============================================================================
// Types
// ============================================================================

export interface DownlinkResult {
  port: number;
  bytes: number[];
  hex: string;
  hexNoSpaces: string;
  base64: string;
  description: string;
  vsm_config?: Record<string, number>;
}

export interface BeaconConfig {
  ssid?: string;
  password?: string;
  mac?: string;
  channel?: number;
  power?: number;
  rate?: string | number;
  interval?: number;
  jitter?: number;
  protocol?: string | number;
  enabled?: boolean;
  apply?: boolean;
}

/**
 * A single configurable input field parsed from a VSM mapData schema string.
 * Only "M input" lines produce VsmField entries — "M output" lines are
 * read-only device outputs and are ignored for downlink purposes.
 */
export interface VsmField {
  /** Field name as it appears in the schema (e.g. "tempHysteresis"). */
  name: string;
  /** Numeric field ID sent in the downlink payload (1 byte). */
  id: number;
  /**
   * Scaling factor applied when encoding a user-facing value to the wire
   * integer.  encodedValue = Math.round(userValue / translate).
   * Defaults to 1 (no scaling) when the schema omits or specifies 0.
   */
  translate: number;
}

// ============================================================================
// VSM mapData schema parser
// ============================================================================

/**
 * Parse the VSM mapData schema string stored in data.vsm.schema.
 *
 * Each line has the form:
 *   M [input|output] fieldName id_decimal 0xhex  translateFactor
 *
 * Only "M input" lines are returned — they represent fields the device
 * accepts via downlink.  "M output" lines are device-reported values and
 * are skipped.
 *
 * @example
 * const fields = parseVsmSchema(device.vsmSchema ?? '');
 */
export function parseVsmSchema(mapData: string): VsmField[] {
  return mapData
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('M input '))
    .map((line) => {
      // "M input tempHysteresis 180 0xb4  0.1"
      //   0    1        2        3    4    5
      const parts = line.split(/\s+/);
      const name = parts[2];
      const id = parseInt(parts[3], 10);
      const translate = parseFloat(parts[5]);
      return {
        name,
        id,
        translate: isNaN(translate) || translate === 0 ? 1 : translate,
      };
    })
    .filter((f) => f.name && !isNaN(f.id));
}

// ============================================================================
// Dots board field encoder (Port 2)
// ============================================================================

/**
 * Encode a single VsmField value to its wire bytes.
 *
 * Format: [id: 1 byte][value: 4 bytes big-endian signed]
 *
 * The user-facing value is divided by the field's translate factor before
 * being packed — e.g. a tempHysteresis of 1.0 °C with translate=0.1 is
 * stored as the integer 10 on the wire.
 */
export function encodeDotsField(field: VsmField, userValue: number): number[] {
  const encoded = Math.round(userValue / field.translate);
  return [
    field.id,
    (encoded >> 24) & 0xff,
    (encoded >> 16) & 0xff,
    (encoded >> 8) & 0xff,
    encoded & 0xff,
  ];
}

// ============================================================================
// Utilities
// ============================================================================

export function toBase64(bytes: number[]): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('base64');
  } else if (typeof btoa !== 'undefined') {
    return btoa(String.fromCharCode.apply(null, bytes));
  }
  throw new Error('No base64 encoder available');
}

export function toHexString(bytes: number[], separator = ' '): string {
  return bytes.map((b) => b.toString(16).padStart(2, '0').toUpperCase()).join(separator);
}

export function fromHexString(hexString: string): number[] {
  const hex = hexString.replace(/[\s:-]/g, '');
  const bytes: number[] = [];
  for (let i = 0; i < hex.length; i += 2) {
    bytes.push(parseInt(hex.substring(i, i + 2), 16));
  }
  return bytes;
}

function buildPayload(commands: number[][]): number[] {
  return [commands.length, ...commands.flat()];
}

// ============================================================================
// ESP32 Beacon Command Generators (Port 48)
// ============================================================================

export function cmdSetBeaconMode(enabled: boolean): number[] {
  return [CMD.SET_BEACON_MODE, enabled ? 0x01 : 0x00];
}

export function cmdSetMac(mac: string): number[] {
  const macBytes = fromHexString(mac);
  if (macBytes.length !== 6) throw new Error('MAC must be 6 bytes');
  if (macBytes[0] & 0x01) throw new Error('MAC must be unicast');
  return [CMD.SET_MAC, ...macBytes];
}

export function cmdSetSsid(ssid: string): number[] {
  if (ssid.length < 1 || ssid.length > 32) throw new Error('SSID must be 1-32 chars');
  const ssidBytes = Array.from(ssid).map((c) => c.charCodeAt(0));
  return [CMD.SET_SSID, ssid.length, ...ssidBytes];
}

export function cmdSetPassword(password: string): number[] {
  if (password.length < 8 || password.length > 63) throw new Error('Password must be 8-63 chars');
  const passwordBytes = Array.from(password).map((c) => c.charCodeAt(0));
  return [CMD.SET_PASSWORD, password.length, ...passwordBytes];
}

export function cmdSetBeaconInterval(intervalMs: number): number[] {
  if (intervalMs < 40 || intervalMs > 1000) throw new Error('Interval must be 40-1000 ms');
  return [CMD.SET_BEACON_INTERVAL, (intervalMs >> 8) & 0xff, intervalMs & 0xff];
}

export function cmdSetOutputPower(powerDbm: number): number[] {
  if (powerDbm < 0 || powerDbm > 26) throw new Error('Power must be 0-26 dBm');
  return [CMD.SET_OUTPUT_POWER, powerDbm];
}

export function cmdSetChannel(channel: number): number[] {
  if (channel < 1 || channel > 13) throw new Error('Channel must be 1-13');
  return [CMD.SET_CHANNEL, channel];
}

export function cmdSetProtocol(protocol: string | number): number[] {
  let protocolByte: number;
  if (typeof protocol === 'string') {
    const upper = protocol.toUpperCase();
    protocolByte = WIFI_PROTOCOL[upper as keyof typeof WIFI_PROTOCOL] ?? 0;
    if (!protocolByte) {
      if (upper.includes('B')) protocolByte |= WIFI_PROTOCOL.B;
      if (upper.includes('G')) protocolByte |= WIFI_PROTOCOL.G;
      if (upper.includes('N')) protocolByte |= WIFI_PROTOCOL.N;
    }
  } else {
    protocolByte = protocol;
  }
  if (protocolByte < 1 || protocolByte > 7) throw new Error('Protocol must be 1-7');
  return [CMD.SET_PROTOCOL, protocolByte];
}

export function cmdSetDataRate(rate: string | number): number[] {
  const rateKey = String(rate).toUpperCase();
  const rateByte = VALID_DATA_RATES[rateKey] ?? VALID_DATA_RATES[rate];
  if (rateByte === undefined) throw new Error(`Invalid rate: ${rate}`);
  return [CMD.SET_DATA_RATE, rateByte];
}

export function cmdSetBeaconJitter(jitterMs: number): number[] {
  if (jitterMs < 0 || jitterMs > 10) throw new Error('Jitter must be 0-10 ms');
  return [CMD.SET_BEACON_JITTER, jitterMs];
}

export function cmdGetStatus(): number[] {
  return [CMD.GET_STATUS];
}

export function cmdApplySettings(): number[] {
  return [CMD.APPLY_SETTINGS];
}

export function cmdResetToFallback(): number[] {
  return [CMD.RESET_TO_FALLBACK];
}

// ============================================================================
// Downlink Generators
// ============================================================================

export function generateDownlink(commands: number[][]): DownlinkResult {
  const bytes = buildPayload(commands);
  const hex = toHexString(bytes, '');
  const base64 = toBase64(bytes);
  return {
    port: LORA_PORT_BEACON,
    bytes,
    hex: toHexString(bytes),
    hexNoSpaces: hex,
    base64,
    description: `Hex: ${hex}, port ${LORA_PORT_BEACON}. Base64: ${base64}, port ${LORA_PORT_BEACON}`,
  };
}

// ============================================================================
// Port 7 Persistent Downlink Generator
// ============================================================================

/**
 * Pack a 32-bit integer as 4 bytes big-endian.
 * Works correctly for both signed (field values) and unsigned (CRCs) inputs.
 */
function pack32BE(value: number): number[] {
  return [(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff];
}

/**
 * Standard IEEE 802.3 CRC32 (polynomial 0xEDB88320, reflected).
 */
function crc32(bytes: number[]): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let i = 0; i < 8; i++) {
      crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/**
 * Generate a port 7 persistent customization downlink.
 *
 * Wire format (matches dots-config-application packer.js):
 *   [appCrc32: 4B BE]
 *   ([port2: 1B][byteLen: 1B][fields: N×5B])+
 *   [00]
 *   [crc32: 4B BE]
 *
 * Each field segment (port 2 slice): [id: 1B][floor(value/translate): 4B BE signed]
 * Fields are sorted alphabetically before packing, batched up to 15 per segment.
 *
 * @param appCrc  - rulesCrc32 of the device firmware (unsigned 32-bit)
 * @param fields  - All schema fields for the selected CRC group
 * @param enabledValues - Map of field name → user-facing numeric value (already filtered to enabled)
 */
export function generatePersistentDownlink(
  appCrc: number,
  fields: VsmField[],
  enabledValues: Record<string, number>
): DownlinkResult {
  // Filter to enabled fields and sort alphabetically (as packer.js does)
  const activeFields = fields
    .filter((f) => enabledValues[f.name] !== undefined)
    .sort((a, b) => a.name.localeCompare(b.name));

  if (activeFields.length === 0) throw new Error('No fields enabled');

  // Pack field bytes into port-2 segments, matching packer.js batching logic:
  // append while current segment is ≤ 15 bytes (3 fields × 5B); start new after 4 fields.
  // Condition from packer.js: data.length <= 15*2 (hex chars) before appending.
  const port2Segments: number[][] = [];
  for (const field of activeFields) {
    const encoded = Math.floor(enabledValues[field.name] / field.translate);
    const fieldBytes = [field.id, ...pack32BE(encoded)]; // 5 bytes = 10 hex chars
    const last = port2Segments[port2Segments.length - 1];
    if (last && last.length <= 15) {
      // last.length ≤ 15 bytes (≤ 3 fields) → append (mirrors packer.js `<= 15*2` hex check)
      last.push(...fieldBytes);
    } else {
      port2Segments.push([...fieldBytes]);
    }
  }

  // Build envelope body: [appCrc32] + segments
  const body: number[] = [...pack32BE(appCrc)];
  for (const seg of port2Segments) {
    body.push(LORA_PORT_DOTS, seg.length, ...seg);
  }

  // CRC32 over the body (before terminator)
  const checksum = crc32(body);

  // Final payload: body + 00 + checksum
  const allBytes = [...body, 0x00, ...pack32BE(checksum)];

  return {
    port: LORA_PORT_PERSISTENT,
    bytes: allBytes,
    hex: toHexString(allBytes),
    hexNoSpaces: toHexString(allBytes, ''),
    base64: toBase64(allBytes),
    description: `Port ${LORA_PORT_PERSISTENT}: persistent config appCrc=0x${(appCrc >>> 0).toString(16).padStart(8, '0')}`,
    vsm_config: { ...enabledValues },
  };
}

export function generateConfigDownlink(config: BeaconConfig): DownlinkResult {
  const commands: number[][] = [];

  if (config.ssid !== undefined) commands.push(cmdSetSsid(config.ssid));
  if (config.password !== undefined) commands.push(cmdSetPassword(config.password));
  if (config.mac !== undefined) commands.push(cmdSetMac(config.mac));
  if (config.channel !== undefined) commands.push(cmdSetChannel(config.channel));
  if (config.power !== undefined) commands.push(cmdSetOutputPower(config.power));
  if (config.rate !== undefined) commands.push(cmdSetDataRate(config.rate));
  if (config.interval !== undefined) commands.push(cmdSetBeaconInterval(config.interval));
  if (config.jitter !== undefined) commands.push(cmdSetBeaconJitter(config.jitter));
  if (config.protocol !== undefined) commands.push(cmdSetProtocol(config.protocol));
  if (config.enabled !== undefined) commands.push(cmdSetBeaconMode(config.enabled));
  if (config.apply !== false) commands.push(cmdApplySettings());

  if (commands.length === 0) throw new Error('No config provided');

  return generateDownlink(commands);
}
